import { useEffect, useMemo, useState } from 'react';
import LoadingButton from '../LoadingButton';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { states } from '../../constant/constant';
import { getShippingAddress } from '../../utils/formatAddress';
import showToast from '../ToastComponent';
import {
  GuestShippingDetailsProps,
  ShippingInfo,
  UserInfoResponse,
} from '../../interface/interface';
import { saveGuestShippingInfo } from '../../service/saveShippingInfoForGuest';
import { getGuestUserDetails } from '../../service/getGuestUserDetails';

const GuestShippingDetails = ({
  cart,
  callCart,
  shippingEditDetailEnabled,
  editShippingEnabled
}: GuestShippingDetailsProps) => {
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    province: '',
    zip: '',
    country: 'Australia',
    specialDeliveryInstructions: '',
    email: '',
    mobile: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [createdUser, setCreatedUser] = useState<UserInfoResponse | null>(null);

  useEffect(() => {
    getGuestUserDetails().then((res) => {
      if (!res?.user) return
      setShippingInfo({
        ...shippingInfo,
        country: 'Australia',
        firstName: res.user.first_name,
        lastName: res.user.last_name,
        email: res.user.email,
        mobile: res.user.phone.mobile,
      } as ShippingInfo)
      shippingEditDetailEnabled(false)
    })
  }, [])

  const addGuestShippingSubmit = async () => {
    setLoading(true); // Validate required fields
    if (!shippingInfo.firstName.trim() || !shippingInfo.lastName.trim()) {
      showToast({
        message: 'Please enter a First and Last name',
        type: 'warning',
      });
      setLoading(false);
      return;
    }

    if (
      !shippingInfo.address1.trim() ||
      !shippingInfo.city.trim() ||
      !shippingInfo.zip.trim()
    ) {
      showToast({
        message: 'Please fill in all required address fields',
        type: 'warning',
      });
      setLoading(false);
      return;
    }

    shippingEditDetailEnabled(true);
    try {
      const response = await saveGuestShippingInfo({
        ...shippingInfo,
        userId: createdUser?.id,
      });
      if (typeof response === 'number') {
        showToast({
          message: 'Error while saving shipping details',
          type: 'warning',
        });
        shippingEditDetailEnabled(false);
        setLoading(false);
        return false;
      }
      setCreatedUser(response.data);
      await callCart();
      shippingEditDetailEnabled(false);
      setLoading(false);
      return true;
    } catch (error: any) {
      console.log(`Error occured while saving checkout ${error}`);
      shippingEditDetailEnabled(false);
      setLoading(false);
      return false;
    }
  };

  const isFormValid = () => {
    return (
      shippingInfo.firstName.trim() &&
      shippingInfo.lastName.trim() &&
      shippingInfo.address1?.trim() &&
      shippingInfo.city?.trim() &&
      shippingInfo.zip?.trim()
    );
  };

  const disabledFlag = useMemo(
    () => !editShippingEnabled,
    [editShippingEnabled, cart],
  );

  return (
    <div className="mb-8">
      <h3 className="text-sm xl:text-base font-semibold text-primary-blue-500 mb-4">
        Shipping Details
      </h3>

      <form className="w-full">
        <div className="flex flex-wrap -mx-3 mb-6">
          <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
            <label
              className="block uppercase tracking-wide text-primary-blue-500 text-xs font-bold mb-2"
              htmlFor="shipping-first-name"
            >
              First Name *
            </label>
            <input
              className={`appearance-none block w-full  
              text-gray-700  rounded py-3 px-4 mb-3 leading-tight 
               ${disabledFlag
                  ? 'cursor-not-allowed border-0 bg-gray-200 opacity-75'
                  : 'border border-gray-200 focus:outline-none focus:bg-white focus:border-gray-500 shadow bg-white '
                }`}
              id="shipping-first-name"
              type="text"
              placeholder="First Name"
              value={shippingInfo.firstName}
              onChange={(e) =>
                setShippingInfo((prev) => ({
                  ...prev,
                  firstName: e.target.value,
                }))
              }
              required
              disabled={disabledFlag}
            />
          </div>
          <div className="w-full md:w-1/2 px-3">
            <label
              className="block uppercase tracking-wide text-primary-blue-500 text-xs font-bold mb-2"
              htmlFor="shipping-last-name"
            >
              Last Name *
            </label>
            <input
              className={`appearance-none block w-full  
              text-gray-700  rounded py-3 px-4 mb-3 leading-tight 
               ${disabledFlag
                  ? 'cursor-not-allowed border-0 bg-gray-200 opacity-75'
                  : 'border border-gray-200 focus:outline-none focus:bg-white focus:border-gray-500 shadow bg-white '
                }`}
              id="shipping-last-name"
              type="text"
              placeholder="Last Name"
              value={shippingInfo.lastName}
              onChange={(e) =>
                setShippingInfo((prev) => ({
                  ...prev,
                  lastName: e.target.value,
                }))
              }
              required
              disabled={disabledFlag}
            />
          </div>
        </div>

        <div className="flex flex-wrap -mx-3 mb-6">
          <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
            <label
              className="block uppercase tracking-wide text-primary-blue-500 text-xs font-bold mb-2"
              htmlFor="guest-email"
            >
              Email Address *
            </label>
            <input
              className={`appearance-none block w-full  
              text-gray-700  rounded py-3 px-4 mb-3 leading-tight 
               ${disabledFlag
                  ? 'cursor-not-allowed border-0 bg-gray-200 opacity-75'
                  : 'border border-gray-200 focus:outline-none focus:bg-white focus:border-gray-500 shadow bg-white '
                }`} id="guest-email"
              type="email"
              placeholder="your.email@example.com"
              value={shippingInfo.email}
              onChange={(e) => {
                setShippingInfo((prev) => ({
                  ...prev,
                  email: e.target.value,
                }));
              }}
              required
              disabled={disabledFlag}
            />
          </div>
          <div className="w-full md:w-1/2 px-3">
            <label
              className="block uppercase tracking-wide text-primary-blue-500 text-xs font-bold mb-2"
              htmlFor="guest-mobile"
            >
              Mobile Number *
            </label>
            <input
              className={`appearance-none block w-full  
              text-gray-700  rounded py-3 px-4 mb-3 leading-tight 
               ${disabledFlag
                  ? 'cursor-not-allowed border-0 bg-gray-200 opacity-75'
                  : 'border border-gray-200 focus:outline-none focus:bg-white focus:border-gray-500 shadow bg-white '
                }`} id="guest-mobile"
              type="tel"
              placeholder="+61 4XX XXX XXX"
              value={shippingInfo.mobile}
              onChange={(e) => {
                setShippingInfo((prev) => ({
                  ...prev,
                  mobile: e.target.value,
                }));
              }}
              required
              disabled={disabledFlag}
            />
          </div>
        </div>

        <AddressAutocomplete
          addressRefValue={shippingInfo.address1}
          address1ManualSetter={(address1) => {
            setShippingInfo((prev) => ({ ...prev, address1 }));
          }}
          state={{
            city: shippingInfo.city,
            country: shippingInfo.country,
            zip: shippingInfo.zip,
            state:
              states.find((x) => x.abbriviation === shippingInfo.province)
                ?.name ||
              states.find((x) => x.name === shippingInfo.province)?.name ||
              shippingInfo.province,
            address2: shippingInfo.address2 ?? '',
            specialDeliveryInstructions:
              shippingInfo.specialDeliveryInstructions ?? '',
            streetNumber: '',
            streetName: '',
            unit: '',
          }}
          setter={(data) => {
            setShippingInfo((prev) => ({
              ...prev,
              city: data.city,
              country: data.country,
              zip: data.zip,
              province: data.state,
              address2: data.address2 ?? '',
              specialDeliveryInstructions:
                data.specialDeliveryInstructions ?? '',
              address1: data?.streetName
                ? getShippingAddress(data)
                : prev.address1,
            }));
          }}
          freezeAddressFields={disabledFlag}
        />

        {!disabledFlag && (
          <div className="mt-2 w-full flex">
            <LoadingButton
              isLoading={loading}
              buttonText={
                cart?.shipping_address?.address1
                  ? 'Modify Shipping Details'
                  : 'Add Shipping Details'
              }
              buttonIcon={<>{'🚚'}</>}
              isDisabled={!isFormValid()}
              onClick={addGuestShippingSubmit}
              className="w-full md:w-1/3 xl:w-max"
            />
          </div>
        )}
      </form>
    </div>
  );
};

export default GuestShippingDetails;
